import { GMXExpandTileBase } from '../expand-tile-base/expand-tile-base'
import { GMXFlipbook } from '../../../flipbook/flipbook'
import { TrimModel, ExteriorColorModel, InteriorSeatTrimsModel } from '../../../../models/vehicle-trim.model'
import { sanitizeInput, sanitizeUrl } from '../../../../utils/sanitize'
import { dispatchGbaalLinkClickEvent } from '../../../../utils/dispatchGbaalLinkClickEvent'
import { getEntryPresetParams } from '../../../../utils/entry-presets'

/**
 * Tile - Colorizer
 */
export class GMXColorizerTile extends GMXExpandTileBase {
	collapsedContentContainerEl: HTMLElement
	collapsedColorSwatchContainerEl: HTMLElement
	collapsedBgImgContainerEl: HTMLElement
	collapsedHeadingEl: HTMLElement
	expandedContentContainer: HTMLElement
	expandedColorNameEl: HTMLElement
	expandedColorSwatchContainerEl: HTMLElement
	expandedTrimSelectorEl: HTMLElement
	expandedTrimTabs: HTMLElement
	vehicleOverlayImgContainerEl: HTMLElement
	tileDescriptionEl: HTMLElement
	flipbookEl: GMXFlipbook
	interiorExteriorToggleBtn: HTMLButtonElement
	exteriorViewEl: HTMLElement
	interiorViewEl: HTMLElement

	private boundInteriorExteriorToggleClickHandler = this._handleInteriorExteriorToggleClick.bind(this)
	private boundSwatchContainerClickHandler = this._handleSwatchContainerClickHandler.bind(this)
	private boundTrimSelectorChangedHandler = this._handleTrimSelectorChange.bind(this)

	get trimDataEndpointUrl(): string {
		return this.getAttribute('data-trims-data-endpoint')
	}

	get labelData(): Record<string, string> {
		let retData: Record<string, string> = {}

		try {
			retData = this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
		} catch (error) {
			window.console.error(`ERROR:  Colorizer Tile::Failed to parse label data: ${error}`)
		}

		return retData
	}

	get trimsFilter(): string[] {
		const filters = this.getAttribute('data-trims-filter')

		if (filters) {
			return JSON.parse(filters)
		} else {
			return []
		}
	}

	private activeExteriorColorId: string
	get activeExteriorColor(): string {
		return this.activeExteriorColorId
	}
	set activeExteriorColor(color: string) {
		this.activeExteriorColorId = color
		this.setAttribute('data-active-exterior-color', color)
	}

	private activeInteriorColorId: string
	get activeInteriorColor(): string {
		return this.activeInteriorColorId
	}
	set activeInteriorColor(color: string) {
		this.activeInteriorColorId = color
		this.setAttribute('data-active-interior-color', color)
	}

	private activeTrimId: string
	get activeTrim(): string {
		return this.activeTrimId
	}
	set activeTrim(trim: string) {
		this.activeTrimId = trim
		this.setAttribute('data-active-trim', trim)
	}

	private collapsedAnchorPositionValue: string
	get collapsedAnchorPosition(): string {
		return this.collapsedAnchorPositionValue
	}
	set collapsedAnchorPosition(anchorPosition: string) {
		this.collapsedAnchorPositionValue = anchorPosition
		this.setAttribute('data-collapsed-anchor-position', anchorPosition)
	}

	/**
	 * Holds the data for the trims
	 * @private
	 */
	private trimsJSON: TrimModel[]

	private initialView: 'interior' | 'exterior'

	private uniqueInstanceId: string

	constructor() {
		super()

		this.uniqueInstanceId = window.crypto.randomUUID()
	}

	async connectedCallback(): Promise<void> {
		super.connectedCallback()

		this.collapsedContentContainerEl = this.querySelector('.collapsed-content-container')
		this.collapsedColorSwatchContainerEl = this.collapsedContentContainerEl.querySelector('.colorizer-swatches')
		this.collapsedBgImgContainerEl = this.collapsedContentContainerEl.querySelector('.collapsed-bg-img-cntr')
		this.collapsedHeadingEl = this.collapsedContentContainerEl.querySelector('.tile-heading')
		this.tileDescriptionEl = this.collapsedContentContainerEl.querySelector('.tile-description')
		this.expandedContentContainer = this.querySelector('.expanded-content-container')
		this.exteriorViewEl = this.expandedContentContainer.querySelector('.exterior-view')
		this.interiorViewEl = this.expandedContentContainer.querySelector('.interior-view')
		this.interiorExteriorToggleBtn = this.expandedContentContainer.querySelector('button.interior-exterior-toggle')
		this.expandedColorNameEl = this.expandedContentContainer.querySelector('.color-name')
		this.expandedColorSwatchContainerEl = this.expandedContentContainer.querySelector('.expanded-color-swatches')
		this.vehicleOverlayImgContainerEl = this.querySelector('.vehicle-image-overlay-container')
		this.expandedTrimSelectorEl = this.expandedContentContainer.querySelector('.trim-selector')
		this.flipbookEl = this.vehicleOverlayImgContainerEl.querySelector('gmx-flipbook') as GMXFlipbook
	}

	disconnectedCallback(): void {
		if (this.trimsJSON.length) {
			this.collapsedColorSwatchContainerEl.removeEventListener('click', this.boundSwatchContainerClickHandler)
			this.expandedColorSwatchContainerEl.removeEventListener('click', this.boundSwatchContainerClickHandler)
			this.expandedTrimTabs.removeEventListener('gmxTabValueChange', this.boundTrimSelectorChangedHandler)

			if (this.interiorExteriorToggleBtn) {
				this.interiorExteriorToggleBtn.removeEventListener(
					'click',
					this.boundInteriorExteriorToggleClickHandler
				)
			}
		}
	}

	/**
	 * Override of the base method to initialize the tile after the observation callback is triggered and the element is in the DOM
	 */
	handleObservationCallback(): void {
		super.handleObservationCallback()
		this.initTile()
	}

	/**
	 * Initializes the tile by fetching the trim data, setting the active trim and colors, building the trim selector tabs if needed, setting the initial view, building the color swatches for the initial view, and adding event listeners for user interactions
	 */
	async initTile(): Promise<void> {
		// First, get the compare trim data JSON
		this.trimsJSON = await this.getTrimData(this.trimDataEndpointUrl)

		if (this.trimsJSON.length) {
			const defaultVDMTrimCode = this.trimsJSON.find(trim => trim.trimDefaults?.isDefaultTrim === true)?.code

			// A valid `?trim=` entry-preset parameter takes precedence over the authored default trim.
			const presetTrim = getEntryPresetParams().trim
			const presetTrimCode =
				presetTrim && this.trimsJSON.find(trim => trim.code === presetTrim) ? presetTrim : null

			if (presetTrimCode) {
				this.activeTrim = presetTrimCode
			} else if (this.getAttribute('data-active-trim')) {
				this.activeTrim = this.trimsJSON.find(trim => trim.code === this.getAttribute('data-active-trim'))
					? this.getAttribute('data-active-trim')
					: defaultVDMTrimCode || this.trimsJSON[0].code
			} else {
				this.activeTrim = defaultVDMTrimCode || this.trimsJSON[0].code
			}

			const activeTrimInfo = this.trimsJSON.find(trim => trim.code === this.activeTrim)

			// Set the active exterior color to the default exterior color if it exists, otherwise use the first exterior color in the list
			const activeExteriorColor = activeTrimInfo.exteriorColors.find(
				color => color.colorCode === activeTrimInfo.trimDefaults?.defaultExteriorColorCode
			)?.colorCode
			this.activeExteriorColor = activeExteriorColor || activeTrimInfo.exteriorColors[0]?.colorCode

			// Set the active interior color to the default interior trim if it exists, otherwise use the first interior trim in the list
			const activeInteriorColor = activeTrimInfo.interiorSeatTrims.find(
				trim => trim.trimCode === activeTrimInfo.trimDefaults?.defaultSeatTrimCode
			)?.trimCode
			this.activeInteriorColor = activeInteriorColor || activeTrimInfo.interiorSeatTrims[0]?.trimCode

			if (this.trimsJSON.length > 1) {
				this._createTrimSelectorTabs()
			}

			if (!this.hasAttribute('data-current-view')) {
				this.setAttribute(
					'data-current-view',
					this.interiorViewEl && !this.exteriorViewEl ? 'interior' : 'exterior'
				)
			} else if (this.getAttribute('data-current-view') === 'interior' && !this.interiorViewEl) {
				this.setAttribute('data-current-view', 'exterior')
			} else if (this.getAttribute('data-current-view') === 'exterior' && !this.exteriorViewEl) {
				this.setAttribute('data-current-view', 'interior')
			}

			if (this.hasAttribute('data-collapsed-anchor-position')) {
				this.collapsedAnchorPosition = this.getAttribute('data-collapsed-anchor-position')
			}

			const currentView = this.getAttribute('data-current-view') === 'interior' ? 'interior' : 'exterior'
			if (currentView === 'interior') {
				if (this.interiorExteriorToggleBtn) {
					this.interiorExteriorToggleBtn.innerText = this.labelData['i18n_exterior'] || 'Label Missing'
				}

				this.initialView = 'interior'
				this._buildColorSwatches(this.activeTrim, 'interior')
			} else {
				if (this.interiorExteriorToggleBtn) {
					this.interiorExteriorToggleBtn.innerText = this.labelData['i18n_interior'] || 'Label Missing'
				}

				this.initialView = 'exterior'
				this._buildColorSwatches(this.activeTrim, 'exterior')
			}

			this.collapsedColorSwatchContainerEl.addEventListener('click', this.boundSwatchContainerClickHandler)
			this.expandedColorSwatchContainerEl.addEventListener('click', this.boundSwatchContainerClickHandler)

			if (this.interiorExteriorToggleBtn) {
				this.interiorExteriorToggleBtn.addEventListener('click', this.boundInteriorExteriorToggleClickHandler)
			}
		}
	}

	/**
	 * Override of the base method to handle when the expand button is clicked. Moves elements around if needed based on the initial view and current view to ensure the correct elements are in the expanded view, and applies necessary styles for the transition
	 * @param e
	 */
	handleExpandBtnClicked(e: Event): void {
		const target = e.target as HTMLElement

		if (
			!(
				target &&
				(target.classList.contains('gmx-controls-container') || target.closest('.gmx-controls-container'))
			) &&
			!(
				target &&
				(target.matches('[data-toast-uri],[data-toast-msg]') ||
					target.closest('[data-toast-uri],[data-toast-msg]'))
			)
		) {
			if (this.initialView === 'exterior') {
				this.vehicleOverlayImgContainerEl.style.minWidth = `${this.vehicleOverlayImgContainerEl.clientWidth}px`
				this.exteriorViewEl?.appendChild(this.vehicleOverlayImgContainerEl)
			} else if (this.getAttribute('data-current-view') === 'exterior') {
				const color = this.trimsJSON
					.find(trim => trim.code === this.activeTrim)
					?.exteriorColors.find(color => color.colorCode === this.activeExteriorColor)
				if (color?.colorizerOverlayTextInvert) {
					this.classList.add('gmx-invert')
				} else {
					this.classList.remove('gmx-invert')
				}
			}
		}

		super.handleExpandBtnClicked(e)
	}

	/**
	 * Override of the base method to handle when the close button is clicked in the expanded view. Resets the view to the initial state and moves elements back to their original containers if needed
	 */
	handleExpandContentCloseBtnClicked(): void {
		super.handleExpandContentCloseBtnClicked()

		if (this.initialView === 'exterior') {
			if (this.getAttribute('data-current-view') === 'interior') {
				this.setAttribute('data-current-view', 'exterior')
				this._buildColorSwatches(this.activeTrim, 'exterior')
			}

			this.vehicleOverlayImgContainerEl.style.minWidth = ''
			this.appendChild(this.vehicleOverlayImgContainerEl)
		} else {
			if (this.getAttribute('data-current-view') === 'exterior') {
				this._buildColorSwatches(this.activeTrim, 'interior')
			}

			this.setAttribute('data-current-view', 'interior')
			this.classList.add('gmx-invert')
		}
	}

	/**
	 * Callback for when the swatch container (for colors) is clicked
	 * @param e {Event}
	 * @private
	 */
	private _handleSwatchContainerClickHandler(e: Event): void {
		e.stopPropagation()

		if (e.target instanceof HTMLInputElement) {
			const swatchInputEl = e.target
			const selectedColor = swatchInputEl.value

			if (this.getAttribute('data-current-view') === 'exterior') {
				if (selectedColor) {
					// Listening on `click` (not `change`) so re-selecting the already-active
					// swatch still fires this handler; only dispatch the GBAAL event when the
					// selection actually changed.
					const isNewSelection = selectedColor !== this.activeExteriorColor
					this.activateExteriorColor(selectedColor)

					if (isNewSelection) {
						const color = this.trimsJSON
							.find(trim => trim.code === this.activeTrim)
							?.exteriorColors.find(color => color.colorCode === selectedColor)

						if (color) {
							dispatchGbaalLinkClickEvent(this, swatchInputEl, { interactionType: 'click' }, { exteriorColor: color.colorName })
						}
					}
				} else {
					window.console.error(`Selected color (${selectedColor}) not found`)
				}
			} else {
				if (selectedColor) {
					const isNewSelection = selectedColor !== this.activeInteriorColor
					this.activateInteriorTrim(selectedColor)

					if (isNewSelection) {
						const interiorTrim = this.trimsJSON
							.find(trim => trim.code === this.activeTrim)
							?.interiorSeatTrims.find(seatTrim => seatTrim.trimCode === selectedColor)

						if (interiorTrim) {
							dispatchGbaalLinkClickEvent(this, swatchInputEl, { interactionType: 'click' }, { interiorColor: interiorTrim.trimName })
						}
					}
				} else {
					window.console.error(`Selected interior trim (${selectedColor}) not found`)
				}
			}
		}
	}

	/**
	 * Callback for when the trim selector tab value is changed. Activates the selected trim
	 * @param e
	 * @private
	 */
	private _handleTrimSelectorChange(e: CustomEvent): void {
		e.stopPropagation()

		const selectedTrimId = e.detail.selectedItem

		if (selectedTrimId) {
			this.activateTrim(selectedTrimId.replace(/^trim-/, ''))
		} else {
			window.console.warn(`WARN:  Color Tile received event that trim changed but received no id`)
		}
	}

	/**
	 * Given a URL of a location of the colorizer instance data, fetch and return the result of the GET
	 * @param url {String} - URL of the location of the colorizer instance data
	 */
	async getTrimData(url: string): Promise<TrimModel[]> {
		try {
			const response = await fetch(url)
			if (!response.ok) throw new Error(`return status: ${response.status}`)

			const data = await response.json()
			const allTrims = data?.data?.trims || []
			if (!this.trimsFilter.length) return allTrims

			const filterSet = new Set(this.trimsFilter)
			return allTrims
				.filter((trim: TrimModel) => filterSet.has(trim.code))
				.sort(
					(a: TrimModel, b: TrimModel) => this.trimsFilter.indexOf(a.code) - this.trimsFilter.indexOf(b.code)
				)
		} catch (error) {
			console.error(`ERROR: Colorizer: Failed to retrieve trim data ${error}`)
			return []
		}
	}

	/**
	 * Activate the given trim by updating the activeTrim, validating that the current active exterior and interior colors are valid for the new trim (if not, default to first color), and rebuilding the swatches for the current view
	 * @param trimId
	 */
	activateTrim(trimId: string): void {
		const trimInfo = this.trimsJSON.find(trim => trim.code === trimId)

		if (trimInfo) {
			this.activeTrim = trimId

			// Default to first color if the current active color on trim is not found
			const exteriorColor = trimInfo.exteriorColors.find(color => color.colorCode === this.activeExteriorColor)
			if (!exteriorColor) {
				this.activeExteriorColor = trimInfo.exteriorColors[0]?.colorCode
			}

			const interiorColor = trimInfo.interiorSeatTrims.find(
				seatTrim => seatTrim.trimCode === this.activeInteriorColor
			)
			if (!interiorColor) {
				this.activeInteriorColor = trimInfo.interiorSeatTrims[0]?.trimCode
			}

			if (this.getAttribute('data-current-view') === 'interior') {
				this._buildColorSwatches(this.activeTrim, 'interior')
			} else {
				this._buildColorSwatches(this.activeTrim, 'exterior')
			}
		} else {
			window.console.error(`ERROR:  trim with id ${trimId} not found`)
		}
	}

	/**
	 * Build the trim selector tabs in the expanded view by creating a gmx-tabs element, building the inner HTML with the trim options, and adding an event listener for when the selected tab changes
	 * @private
	 */
	private _createTrimSelectorTabs(): void {
		this.expandedTrimTabs = document.createElement('gmx-tabs')
		this.expandedTrimTabs.classList.add(
			'gmx-tabs',
			'gmx-tabs-large',
			'dropdown-on-mobile',
			'gmx-tabs-on-colored-bg'
		)
		this.expandedTrimTabs.setAttribute('data-selected-tab-id', `trim-${this.activeTrim}`)
		this.expandedTrimTabs.innerHTML = `
			<span class="selected-dropdown-text">${sanitizeInput(this.trimsJSON.find(trim => trim.code === this.activeTrim)?.title || '')}</span>
			<ul>${this.trimsJSON
				.map(
					trim =>
						`<li>
					<button class="tab-link-btn"
						type="button"
						role="tab"
						data-icon-name="checkmark"
						aria-selected="${trim.code === this.activeTrim ? 'true' : 'false'}"
						aria-controls="trim-${sanitizeInput(trim.code)}">${sanitizeInput(trim.title)}</button>
				</li>`
				)
				.join('')}</ul>`

		this.expandedTrimSelectorEl.appendChild(this.expandedTrimTabs)

		this.expandedTrimTabs.addEventListener('gmxTabValueChange', this.boundTrimSelectorChangedHandler)
	}

	/**
	 * Callback for when the interior/exterior toggle button is clicked. Toggles the view and updates the swatches accordingly
	 * @private
	 */
	private _handleInteriorExteriorToggleClick(): void {
		if (this.getAttribute('data-current-view') === 'interior') {
			if (this.interiorExteriorToggleBtn) {
				this.interiorExteriorToggleBtn.innerText = this.labelData['i18n_interior'] || 'Label Missing'
			}

			this._buildColorSwatches(this.activeTrim, 'exterior')
			this.setAttribute('data-current-view', 'exterior')
		} else {
			if (this.interiorExteriorToggleBtn) {
				this.interiorExteriorToggleBtn.innerText = this.labelData['i18n_exterior'] || 'Label Missing'
			}

			this.classList.add('gmx-invert')
			this._buildColorSwatches(this.activeTrim, 'interior')
			this.setAttribute('data-current-view', 'interior')
		}
	}

	/**
	 * Activate the given exterior color by updating the activeExteriorColor, updating the flipbook images, and updating the swatch selection
	 * @param colorId
	 */
	activateExteriorColor(colorId: string): void {
		this.activeExteriorColor = colorId

		const activeTrim = this.trimsJSON.filter(trim => trim.code === this.activeTrim)[0]
		const color =
			this.trimsJSON
				.filter(trim => trim.code === this.activeTrim)[0]
				.exteriorColors.filter(color => color.colorCode === this.activeExteriorColor)[0] ||
			activeTrim.exteriorColors[0]

		const createNewFlipbook = !this.flipbookEl ? true : false

		const flipbookFilename = `${activeTrim.colorizerFlipbookImgFolderPath.replace(/\/$/g, '')}/${color.colorCode.toLowerCase()}/{framenumber}.${activeTrim.colorizerFlipbookFileExtension}`
		const newFlipbookEl = document.createElement('gmx-flipbook') as GMXFlipbook
		newFlipbookEl.setAttribute('data-number-of-frames', activeTrim.colorizerFlipbookNumberOfFrames.toString())
		newFlipbookEl.setAttribute('data-starting-frame', this.flipbookEl?.currentFrameNumber?.toString() || '1')
		newFlipbookEl.setAttribute('data-image-url-format-mobile', `${flipbookFilename}?imwidth=962`)
		newFlipbookEl.setAttribute('data-image-url-format-desktop', `${flipbookFilename}?imwidth=2560`)

		newFlipbookEl.addEventListener(
			'gmxFlipbookImagesLoaded',
			() => {
				if (this.initialView === 'exterior') {
					const disclosurePath = sanitizeUrl(color.disclosurePath)

					if (disclosurePath) {
						this.tileDescriptionEl.textContent = ''
						const newDisclosureSpanEl = document.createElement('span')
						newDisclosureSpanEl.setAttribute('data-legal-uri', disclosurePath)
						newDisclosureSpanEl.textContent = color.colorName
						this.tileDescriptionEl.appendChild(newDisclosureSpanEl)
					} else {
						this.tileDescriptionEl.textContent = color.colorName
					}
				}

				if (!createNewFlipbook) {
					this.vehicleOverlayImgContainerEl.addEventListener(
						'animationend',
						() => {
							this.vehicleOverlayImgContainerEl.classList.remove('swapping')
							this.collapsedContentContainerEl.style.backgroundColor = color.colorizerBgGradient
							this.expandedContentContainer.style.backgroundColor = color.colorizerBgGradient

							newFlipbookEl.classList.remove('new-color')

							this.vehicleOverlayImgContainerEl.removeChild(this.flipbookEl)
							this.flipbookEl = newFlipbookEl
						},
						{ once: true }
					)
					this.vehicleOverlayImgContainerEl.classList.add('swapping')
				}

				this.collapsedContentContainerEl.style.backgroundColor = color.colorizerBgGradient
				this.expandedContentContainer.style.backgroundColor = color.colorizerBgGradient

				if (color.colorizerOverlayTextInvert) {
					this.classList.add('gmx-invert')
				} else {
					this.classList.remove('gmx-invert')
				}
			},
			{ once: true }
		)

		if (createNewFlipbook) {
			this.flipbookEl = newFlipbookEl
		} else {
			newFlipbookEl.classList.add('new-color')
		}

		this.vehicleOverlayImgContainerEl.appendChild(newFlipbookEl)

		const disclosurePath = sanitizeUrl(color.disclosurePath)
		if (disclosurePath) {
			this.expandedColorNameEl.textContent = ''
			const newDisclosureSpanEl = document.createElement('span')
			newDisclosureSpanEl.setAttribute('data-legal-uri', disclosurePath)
			newDisclosureSpanEl.textContent = color.colorName
			this.expandedColorNameEl.appendChild(newDisclosureSpanEl)
		} else {
			this.expandedColorNameEl.textContent = color.colorName
		}

		if (this.initialView === 'exterior') {
			this.collapsedColorSwatchContainerEl.querySelectorAll('input').forEach(colorInputEl => {
				colorInputEl.checked = colorInputEl.value === color.colorCode
			})
		}

		this.expandedColorSwatchContainerEl.querySelectorAll('input').forEach(colorInputEl => {
			if (colorInputEl.value === color.colorCode) {
				colorInputEl.checked = colorInputEl.value === color.colorCode
			}
		})
	}

	/**
	 * Activate the given interior trim by updating the activeInteriorColor, updating the images, and updating the swatch selection
	 * @param trimCode
	 */
	activateInteriorTrim(trimCode: string): void {
		this.activeInteriorColor = trimCode

		const activeTrim = this.trimsJSON.filter(trim => trim.code === this.activeTrim)[0]
		const interiorTrim =
			activeTrim.interiorSeatTrims.filter(trim => trim.trimCode === this.activeInteriorColor)[0] ||
			activeTrim.interiorSeatTrims[0]

		const disclosurePath = sanitizeUrl(interiorTrim.disclosurePath)
		if (disclosurePath) {
			this.expandedColorNameEl.textContent = ''
			const newDisclosureSpanEl = document.createElement('span')
			newDisclosureSpanEl.setAttribute('data-legal-uri', disclosurePath)
			newDisclosureSpanEl.textContent = interiorTrim.trimName
			this.expandedColorNameEl.appendChild(newDisclosureSpanEl)
		} else {
			this.expandedColorNameEl.textContent = interiorTrim.trimName
		}

		if (this.initialView === 'interior') {
			this.collapsedColorSwatchContainerEl.querySelectorAll('input').forEach(colorInputEl => {
				colorInputEl.checked = colorInputEl.value === interiorTrim.trimCode
			})
		}

		this.expandedColorSwatchContainerEl.querySelectorAll('input').forEach(colorInputEl => {
			colorInputEl.checked = colorInputEl.value === interiorTrim.trimCode
		})

		const currentInteriorPictureEl = this.interiorViewEl.querySelector('picture')

		const newPictureEl = document.createElement('picture')
		newPictureEl.innerHTML = `
			<source media="(min-width: 481px)" srcset="${encodeURI(interiorTrim.primaryImage_path)}">
			<img class="gmx-img-cover" src="${encodeURI(interiorTrim.primaryImage_sm_path)}" alt="${sanitizeInput(interiorTrim.trimName)}">`

		const pictureImgEl = newPictureEl.querySelector('img')
		pictureImgEl.onload = () => {
			if (currentInteriorPictureEl) {
				this.interiorViewEl.removeChild(currentInteriorPictureEl)
			}
		}

		this.interiorViewEl.appendChild(newPictureEl)

		if (this.initialView === 'interior') {
			let pictureClasses = 'gmx-img-cover'
			const anchorPosition = this.collapsedAnchorPositionValue
			if (anchorPosition === 'left' || anchorPosition === 'right') {
				pictureClasses += ' gmx-cover-anchor-' + anchorPosition
			}

			this.collapsedBgImgContainerEl.innerHTML = `
				<picture>
					<source media="(min-width: 481px)" srcset="${encodeURI(interiorTrim.primaryImage_path)}">
					<img class="${pictureClasses}" src="${encodeURI(interiorTrim.primaryImage_sm_path)}" alt="${sanitizeInput(interiorTrim.trimName)}">
				</picture>`
			this.classList.add('gmx-invert')

			const disclosurePath = sanitizeUrl(interiorTrim.disclosurePath)
			if (disclosurePath) {
				this.tileDescriptionEl.textContent = ''
				const newDisclosureSpanEl = document.createElement('span')
				newDisclosureSpanEl.setAttribute('data-legal-uri', disclosurePath)
				newDisclosureSpanEl.textContent = interiorTrim.trimName
				this.tileDescriptionEl.appendChild(newDisclosureSpanEl)
			} else {
				this.tileDescriptionEl.textContent = interiorTrim.trimName
			}
		}

		/* interiorPictureEl.src = interiorTrim.primaryImage_path
		interiorPictureEl.alt = interiorTrim.trimName */
	}

	private _buildColorSwatches(trimId: string, view: 'interior' | 'exterior'): void {
		const trim = this.trimsJSON.find(t => t.code === trimId)
		const colors = view === 'interior' ? trim.interiorSeatTrims : trim.exteriorColors
		const activeColor = view === 'interior' ? this.activeInteriorColor : this.activeExteriorColor

		const collapsedSwatchHTML = `
			<fieldset class="gmx-controls-vehicle controls-vehicle-color">
				${colors
					.map(
						(color: ExteriorColorModel | InteriorSeatTrimsModel) => `
					<label aria-label="${sanitizeInput(('colorName' in color && color.colorName) || ('trimName' in color && color.trimName))}">
						<input name="tile-collapsed-${view}-color-${this.uniqueInstanceId}" type="radio" value="${sanitizeInput(('colorCode' in color && color.colorCode) || ('trimCode' in color && color.trimCode))}" style="--vc-chip-color: ${sanitizeInput(color.colorSwatchHex)};" ${('colorCode' in color && color.colorCode === activeColor) || ('trimCode' in color && color.trimCode === activeColor) ? 'checked' : ''}>
					</label>`
					)
					.join('')}
			</fieldset>`

		const expandedSwatchHTML = `
			<fieldset class="gmx-controls-vehicle controls-vehicle-color">
				${colors
					.map(
						(color: ExteriorColorModel | InteriorSeatTrimsModel) => `
					<label aria-label="${sanitizeInput(('colorName' in color && color.colorName) || ('trimName' in color && color.trimName))}">
						<input name="tile-expanded-${view}-color-${this.uniqueInstanceId}" type="radio" value="${sanitizeInput(('colorCode' in color && color.colorCode) || ('trimCode' in color && color.trimCode))}" style="--vc-chip-color: ${sanitizeInput(color.colorSwatchHex)};" ${('colorCode' in color && color.colorCode === activeColor) || ('trimCode' in color && color.trimCode === activeColor) ? 'checked' : ''}>
					</label>`
					)
					.join('')}
			</fieldset>`

		if (view === 'interior') {
			this.collapsedColorSwatchContainerEl.innerHTML = collapsedSwatchHTML
			this.expandedColorSwatchContainerEl.innerHTML = expandedSwatchHTML
			this.activateInteriorTrim(activeColor)
		} else {
			this.collapsedColorSwatchContainerEl.innerHTML = collapsedSwatchHTML
			this.expandedColorSwatchContainerEl.innerHTML = expandedSwatchHTML
			this.activateExteriorColor(activeColor)
		}
	}
}
if (!customElements.get('gmx-colorizer-tile')) {
	customElements.define('gmx-colorizer-tile', GMXColorizerTile)
}
